import { effect, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private translateService = inject(TranslateService);

  currentLocale = signal<string>(
    localStorage.getItem('dhigSelectedLanguage') ||
      this.translateService.currentLang ||
      this.translateService.defaultLang ||
      'en'
  );

  dateFormat = signal<string>('DD.MM.YY');
  languageChanged = signal<number>(0);

  constructor() {
    this.updateFromLocalStorage();

    effect(() => {
      const subscription = this.translateService.onLangChange.subscribe((event) => {
        this.currentLocale.set(event.lang);
        this.loadDateFormat();
        this.languageChanged.update((val) => val + 1);
      });

      return () => subscription.unsubscribe();
    });

    effect(() => {
      const handleStorageChange = (event: StorageEvent) => {
        if (event.key === 'dhigSelectedLanguage' && event.newValue) {
          this.updateFromLocalStorage();
        }
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    });

    this.loadDateFormat();
  }

  private updateFromLocalStorage(): void {
    const storedLang = localStorage.getItem('dhigSelectedLanguage');
    if (storedLang) {
      this.currentLocale.set(storedLang);
      this.loadDateFormat();
      this.languageChanged.update((val) => val + 1);
    }
  }

  private loadDateFormat(): void {
    this.translateService.get('dateFormats.default').subscribe((format) => {
      if (format && format !== 'dateFormats.default') {
        this.dateFormat.set(format);
      } else {
        this.dateFormat.set('DD.MM.YY');
      }
    });
  }

  formatDate(value: string | Date | null | undefined): string {
    if (!value) return '';

    let year: string;
    let month: string;
    let day: string;

    if (typeof value === 'string') {
      const parts = value.split('-');
      if (parts.length === 3) {
        year = parts[0];
        month = parts[1];
        day = parts[2];
      } else {
        return value;
      }
    } else if (value instanceof Date) {
      year = String(value.getUTCFullYear());
      month = String(value.getUTCMonth() + 1).padStart(2, '0');
      day = String(value.getUTCDate()).padStart(2, '0');
    } else {
      return '';
    }

    const shortYear = year.slice(-2);

    try {
      const format = this.dateFormat();

      return format
        .replace(/YYYY/g, year)
        .replace(/YY/g, shortYear)
        .replace(/MM/g, month)
        .replace(/DD/g, day)
        .replace(/M(?!M)/g, String(parseInt(month, 10)))
        .replace(/D(?!D)/g, String(parseInt(day, 10)));
    } catch (error) {
      console.error('Error formatting date:', error);
      return `${year}-${month}-${day}`;
    }
  }
}
